//@ts-ignore
import { onlyNumeric } from '@origin-digital/normalize';
import { Button, Colors, Heading3, Metrics, Helpers } from '@origin-digital/style-guide';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React from 'react';
import styled from 'styled-components';
import analytics from '../../../../analytics';
import { HiddenField, PostcodeField, Select, TextField } from '../../../../components';
import { CHECK_SERVICEABILITY } from '../../../../queries';
import { currencyTransform } from '../../../../utils/transforms';
import { CalculatorFormProps, CalculatorFormState, CalculatorFormValues } from './CalculatorForm.types';
import { SolarDisqualificationType } from '../../../../analytics/Analytics';
import { validation } from './CalculatorFormConfig';
// @ts-ignore
import calculatorMetaData from '../../calculatorMetaData.json';
import { CalculatorValuesInput } from '../../../../../__generated__/globalTypes';

const Container = styled.div`
  background: ${Colors.WHITE};
  display: flex;
  justify-content: center;
`;

const CardForm = styled(Form)`
  max-width: 360px;
  width: 100%;
  padding: ${Metrics.Spacing.M_Y_SM}px;

  ${Helpers.media.XL`
    max-width: 411px;
  `};
`;

export class CalculatorForm extends React.Component<CalculatorFormProps, CalculatorFormState> {
  constructor(props: CalculatorFormProps) {
    super(props);

    this.submit = this.submit.bind(this);
    this.setAsyncFetching = this.setAsyncFetching.bind(this);
    this.onFieldChange = this.onFieldChange.bind(this);
    this.parseGraphResult = this.parseGraphResult.bind(this);
    this.setDisqualification = this.setDisqualification.bind(this);
    this.recordDisqualification = this.recordDisqualification.bind(this);

    this.state = {
      asyncFetching: false,
      editPostcode: false,
      validPostcode: true,
      triggerSubmit: false,
      currentValues: {},
      metaData: calculatorMetaData
    };
  }

  componentDidUpdate(prevProps: CalculatorFormProps, prevState: CalculatorFormState) {
    const { asyncFetching } = this.state;

    if (asyncFetching !== prevState.asyncFetching) {
      this.setState({
        asyncFetching
      });
    }
  }

  componentDidMount() {
    const { averageBill, postcode, usagePercentDuringDay } = this.initialValues;

    if (averageBill && postcode && usagePercentDuringDay) {
      this.setState({
        triggerSubmit: true
      });
    }
  }

  submit(values: CalculatorFormValues) {
    const { onSubmit } = this.props;
    let _values = { ...values };

    _values.averageBill = _values.averageBill.replace(/\$/g, '');
    if (onSubmit) onSubmit(_values);
  }

  onFieldChange(fieldName: string, value: string): void {
    const { editPostcode, currentValues } = this.state;

    this.setState({
      currentValues: {
        ...currentValues,
        [fieldName]: value
      }
    });

    if (fieldName === 'postcode' && !editPostcode && value.length < 4) {
      this.setState({
        editPostcode: true
      });
    }
  }

  parseGraphResult(data: any): void {
    return data.serviceable;
  }

  setDisqualification(value: boolean): void {
    const { triggerSubmit } = this.state;

    if (triggerSubmit && value) {
      this.submit(this.initialValues);
    }

    this.setState({
      validPostcode: value,
      editPostcode: false,
      triggerSubmit: false
    });
  }

  recordDisqualification(name: string, error: string): void {
    const { currentValues } = this.state;

    analytics.disqualify(name as SolarDisqualificationType, error, {
      postcode: currentValues.postcode
    });
  }

  setAsyncFetching(asyncFetching: boolean): void {
    this.setState({
      asyncFetching
    });
  }

  get initialValues(): CalculatorFormValues {
    const qs: CalculatorValuesInput = document.location.search
      .slice(1)
      .split('&')
      .reduce(
        (_qs, entry) => {
          const [key, value] = entry.split('=');
          _qs[key] = value;
          return _qs;
        },
        {} as { [key: string]: string }
      );

    return {
      postcode: qs.postcode ? qs.postcode : '',
      averageBill: qs.bill ? `$${qs.bill}`.toString() : '',
      hiddenPostcode: '',
      usagePercentDuringDay: qs.mostUsage ? qs.mostUsage : ''
    };
  }

  render() {
    const { metaData, asyncFetching, validPostcode, editPostcode, currentValues } = this.state;
    const { onGraphError, graphError, graphMessage, loading } = this.props;

    const showDisqualification = editPostcode ? editPostcode : validPostcode;

    return (
      <Formik
        initialValues={this.initialValues as CalculatorFormValues}
        validationSchema={validation}
        onSubmit={this.submit.bind(this)}
        render={({ values, isValid }: FormikProps<FormikValues>) => {
          const disableSubmit =
            values.hiddenPostcode === 'false' || (values.hiddenPostcode === '' && !isValid) || editPostcode || !isValid;

          return (
            <Container>
              <CardForm>
                <Heading3 text={metaData.form.content.form.title} marginBottom />
                <Field
                  name={metaData.form.fields.postcode.name}
                  label={metaData.form.fields.postcode.label}
                  component={PostcodeField}
                  productType="SOLAR"
                  showManualAddress={validPostcode}
                  transform={onlyNumeric}
                  maxLength={4}
                  onFieldChange={this.onFieldChange}
                  spinner={asyncFetching}
                  setAsyncFetching={this.setAsyncFetching}
                  parseGraphResult={this.parseGraphResult}
                  graphQuery={CHECK_SERVICEABILITY}
                  graphError={graphError}
                  graphMessage={graphMessage}
                  onGraphError={onGraphError}
                  disqualificationMessage={metaData.form.fields.disqualification}
                  setDisqualification={this.setDisqualification}
                />
                <Field
                  name={metaData.form.fields.hiddenPostcode.name}
                  disqualificationMessage={metaData.form.fields.disqualification}
                  recordDisqualification={this.recordDisqualification}
                  component={HiddenField}
                  marginTopOffset={validPostcode || editPostcode ? 0 : -4}
                  isValid={showDisqualification}
                  onFieldChange={this.onFieldChange}
                />
                <Field
                  name={metaData.form.fields.averageBill.name}
                  component={TextField}
                  label={metaData.form.fields.averageBill.label}
                  helpText={metaData.form.fields.averageBill.helpText}
                  transform={currencyTransform}
                  onFieldChange={this.onFieldChange}
                />
                <Field
                  name={metaData.form.fields.usagePercentDuringDay.name}
                  component={Select}
                  label={metaData.form.fields.usagePercentDuringDay.label}
                  options={metaData.form.fields.usagePercentDuringDay.options}
                  onFieldChange={this.onFieldChange}
                />
                <Button
                  type="submit"
                  data-id="calculateNowButton"
                  disabled={disableSubmit}
                  spinner={loading}
                  text="Calculate now"
                  block={true}
                  onClick={() => analytics.formSubmit('calculate-now', currentValues)}
                />
              </CardForm>
            </Container>
          );
        }}
      />
    );
  }
}
